import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { FaArrowUp, FaArrowDown, FaCalculator, FaFileInvoiceDollar } from 'react-icons/fa';

const ProfitAndLoss = () => {
    const { token } = useAuth();
    const [filterType, setFilterType] = useState('month');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [monthValue, setMonthValue] = useState('');
    const [weekOptions, setWeekOptions] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [, setLastRun] = useState(null);

    const filterOptions = useMemo(() => ([
        { value: 'date', label: 'Date Range' },
        { value: 'month', label: 'Month' },
        { value: 'week', label: 'Week' }
    ]), []);

    useEffect(() => {
        const now = new Date();
        const pad = (val) => String(val).padStart(2, '0');
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        setStartDate(`${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`);
        setEndDate(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`);
        setMonthValue(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
    }, []);

    useEffect(() => {
        const fetchWeeks = async () => {
            try {
                const response = await axios.get(`${rootUrl}/system/valid_weeks?past=24`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data?.success) {
                    const options = (response.data.valid_weeks || []).map((week) => ({
                        value: week,
                        label: week
                    }));
                    setWeekOptions(options);

                    if (response.data.system_week) {
                        const current = options.find((opt) => opt.value === response.data.system_week) || null;
                        setSelectedWeek(current);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch weeks', err);
            }
        };

        if (token) {
            fetchWeeks();
        }
    }, [token]);

    const runReport = async () => {
        setLoading(true);
        setError('');

        try {
            const params = {};
            if (filterType === 'date') {
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
            } else if (filterType === 'month' && monthValue) {
                params.month = monthValue;
            } else if (filterType === 'week' && selectedWeek?.value) {
                params.weekKey = selectedWeek.value;
            }

            const response = await axios.get(`${rootUrl}/api/gl/profit-and-loss`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            if (response.data?.success) {
                setData(response.data.data);
                setLastRun(new Date());
            } else {
                setData(null);
                setError(response.data?.message || 'Failed to load report');
            }
        } catch (err) {
            setData(null);
            setError(err.response?.data?.message || 'Failed to load report');
        } finally {
            setLoading(false);
        }
    };

    // Auto-run on mount or when filter defaults are set
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (token && monthValue && !data && !loading) {
            runReport();
        }
    }, [token, monthValue]);

    const formatCurrency = (value) => {
        const amount = parseFloat(value || 0);
        return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const Section = ({ title, items, total, colorClass = "text-gray-900" }) => (
        <div className="mb-8">
            <h3 className={`text-lg font-bold border-b-2 border-gray-100 pb-2 mb-4 uppercase tracking-wider ${colorClass}`}>
                {title}
            </h3>
            <div className="space-y-2">
                {items.length === 0 ? (
                    <p className="text-sm text-gray-400 italic py-2">No entries for this section</p>
                ) : (
                    items.map((item) => (
                        <div key={item.code} className="flex justify-between items-center py-2 hover:bg-gray-50 px-2 rounded transition-colors group">
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold text-gray-700">{item.name}</span>
                                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-tighter">{item.code}</span>
                            </div>
                            <span className="text-sm font-bold text-gray-800">{formatCurrency(item.balance)}</span>
                        </div>
                    ))
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-100 mt-2">
                    <span className="text-sm font-black uppercase text-gray-500">Total {title}</span>
                    <span className={`text-base font-black ${colorClass}`}>{formatCurrency(total)}</span>
                </div>
            </div>
        </div>
    );

    const SummaryCard = ({ title, amount, icon: Icon, color }) => (
        <div className={`bg-white p-6 rounded-2xl shadow-sm border-l-4 ${color} flex items-center justify-between`}>
            <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{title}</p>
                <p className="text-2xl font-black text-gray-900">{formatCurrency(amount)}</p>
            </div>
            <div className={`p-3 rounded-xl opacity-20 bg-current ${color.replace('border-', 'text-')}`}>
                <Icon size={24} />
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-25">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Profit & Loss Statement</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Detailed analysis of revenues and expenses
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-4 pb-12">
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                        <div className="md:col-span-1">
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Period View</label>
                            <Select
                                options={filterOptions}
                                value={filterOptions.find((opt) => opt.value === filterType)}
                                onChange={(option) => setFilterType(option?.value || 'date')}
                                styles={{ control: (b) => ({ ...b, borderRadius: '12px', padding: '2px' }) }}
                            />
                        </div>

                        {filterType === 'date' && (
                            <>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">From</label>
                                    <input type="date" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">To</label>
                                    <input type="date" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                </div>
                            </>
                        )}

                        {filterType === 'month' && (
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Month</label>
                                <input type="month" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm" value={monthValue} onChange={(e) => setMonthValue(e.target.value)} />
                            </div>
                        )}

                        {filterType === 'week' && (
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Week</label>
                                <Select
                                    options={weekOptions}
                                    value={selectedWeek}
                                    onChange={(option) => setSelectedWeek(option)}
                                    styles={{ control: (b) => ({ ...b, borderRadius: '12px', padding: '2px' }) }}
                                />
                            </div>
                        )}

                        <button
                            onClick={runReport}
                            disabled={loading}
                            className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-black uppercase tracking-widest rounded-xl px-8 py-3 transition-all shadow-lg shadow-orange-100 disabled:opacity-50"
                        >
                            {loading ? 'Crunching...' : 'Generate P&L'}
                        </button>
                    </div>

                    {error && <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold border border-red-100">{error}</div>}
                </div>

                {data && (
                    <div className="animate-fade-in">
                        {/* Summary Dashboard */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <SummaryCard title="Total Revenue" amount={data.revenue.total} icon={FaArrowUp} color="border-green-500" />
                            <SummaryCard title="Gross Profit" amount={data.grossProfit} icon={FaCalculator} color="border-blue-500" />
                            <SummaryCard title="Net Profit" amount={data.netProfit} icon={FaFileInvoiceDollar} color="border-[#FF5733]" />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Left Column: Revenue & COS */}
                            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
                                <Section title="Operating Revenue" items={data.revenue.items} total={data.revenue.total} colorClass="text-green-600" />
                                <Section title="Cost of Sales" items={data.costOfSales.items} total={data.costOfSales.total} colorClass="text-orange-600" />
                                
                                <div className="mt-12 p-6 bg-blue-50 rounded-2xl border border-blue-100 flex justify-between items-center">
                                    <span className="text-sm font-black uppercase text-blue-800">Gross Profit</span>
                                    <span className="text-2xl font-black text-blue-900">{formatCurrency(data.grossProfit)}</span>
                                </div>
                            </div>

                            {/* Right Column: Expenses */}
                            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
                                <Section title="Operating Expenses" items={data.expenses.items} total={data.expenses.total} colorClass="text-red-600" />
                                
                                <div className="mt-6 p-6 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-center mb-8">
                                    <span className="text-sm font-black uppercase text-gray-700">Operating Profit</span>
                                    <span className="text-xl font-black text-gray-900">{formatCurrency(data.operatingProfit)}</span>
                                </div>

                                <Section title="Other Income" items={data.otherIncome.items} total={data.otherIncome.total} colorClass="text-teal-600" />
                                <Section title="Other Expenses" items={data.otherExpenses.items} total={data.otherExpenses.total} colorClass="text-pink-600" />
                            </div>
                        </div>

                        {/* Grand Final Total */}
                        <div className="mt-8 bg-gray-900 rounded-3xl p-8 text-white flex flex-col md:flex-row items-center justify-between shadow-2xl">
                            <div>
                                <h2 className="text-2xl font-black tracking-tight mb-1 uppercase">Net Performance</h2>
                                <p className="text-gray-400 text-sm font-medium italic">Bottom line for the selected period</p>
                            </div>
                            <div className="text-right mt-4 md:mt-0">
                                <p className="text-4xl font-black text-[#FF5733]">{formatCurrency(data.netProfit)}</p>
                                <div className="flex items-center justify-end gap-2 mt-1">
                                    {data.netProfit >= 0 ? (
                                        <span className="flex items-center gap-1 text-green-400 text-[10px] font-black uppercase tracking-widest"><FaArrowUp /> Surplus</span>
                                    ) : (
                                        <span className="flex items-center gap-1 text-red-400 text-[10px] font-black uppercase tracking-widest"><FaArrowDown /> Deficit</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfitAndLoss;
